<template>
    <Dialog 
        v-model:visible="visible" 
        modal 
        :header="`Create Adjustment for ${rowData?.advertiser?.name || 'Advertiser'} - ${rowData?.market}`"
        class="w-[90vw] md:w-[70vw] lg:w-[60vw]"
        @update:visible="(val) => emit('update:visible', val)"
    >
        <div v-if="rowData" class="p-4">
            <div class="text-lg font-semibold mb-4">
                <div v-if="siteLinkData">Publisher: {{ siteLinkData.siteBrandLink.userId.type == 'Company' ? siteLinkData.siteBrandLink.userId.companyName : siteLinkData.siteBrandLink.userId.name }}</div>
                <div v-if="siteLinkData?.siteBrandLink?.spaceAdvertiserLoginId" class="mt-2 text-sm text-gray-600">
                    <div>Username: {{ siteLinkData.siteBrandLink.spaceAdvertiserLoginId.username || 'N/A' }}</div>
                    <div>
                        Login URL: 
                        <a 
                            v-if="siteLinkData.siteBrandLink.spaceAdvertiserLoginId.loginUrl"
                            :href="siteLinkData.siteBrandLink.spaceAdvertiserLoginId.loginUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:text-blue-800 underline"
                        >
                            {{ siteLinkData.siteBrandLink.spaceAdvertiserLoginId.loginUrl }}
                        </a>
                        <span v-else>N/A</span>
                    </div>
                </div>
            </div>
            
            <!-- Adjustment Type Dropdown -->
            <div class="mb-4">
                <label class="block text-sm font-medium mb-2">Adjustment Type</label>
                <Select 
                    v-model="selectedAdjustmentType" 
                    :options="adjustmentTypes" 
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select adjustment type"
                    class="w-full"
                />
            </div>
            
            <!-- Date Picker -->
            <div class="mb-4">
                <label class="block text-sm font-medium mb-2">Date</label>
                <DatePicker 
                    v-model="selectedDate" 
                    showIcon 
                    :showOnFocus="true"
                    class="w-full"
                    placeholder="Select date"
                />
            </div>
            
            <!-- Publisher Deal and Our Deal -->
            <div v-if="siteLinkData" class="mb-4 grid grid-cols-2 gap-4">
                <div class="flex flex-col">
                    <span class="font-bold text-sm text-gray-600 mb-1">Publisher Deal CPA:</span>
                    <span>{{ publisherDealCPA || 'N/A' }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold text-sm text-gray-600 mb-1">Our Deal CPA:</span>
                    <span>{{ ourDealCPA || 'N/A' }}</span>
                </div>
                <!-- Which deal version these rates come from. Only shown for a link
                     whose deal has changed, where "the deal" is ambiguous without it. -->
                <div v-if="effectiveDeal" class="col-span-2 text-xs text-gray-500">
                    Rates effective on {{ selectedDateLabel }} — deal v{{ effectiveDeal.version }}<span
                        v-if="effectiveDeal.effectiveFrom">, in force from {{ effectiveFromLabel }}</span>
                </div>
            </div>
            
            <!-- CPA Adjustment Input (only shown when CPA adjustment is selected) -->
            <div v-if="selectedAdjustmentType === 'cpa'" class="mb-4">
                <label class="block text-sm font-medium mb-2">CPA (count)</label>
                <InputNumber
                    v-model="cpaAdjustment"
                    :minFractionDigits="0"
                    :maxFractionDigits="2"
                    class="w-full"
                    placeholder="Enter CPA count (negatives allowed)"
                />
                <!-- Current vs after. The delta alone never answered the question
                     the operator actually has - "what will this row read once I
                     save?" - so both sides are shown with the change between them. -->
                <div v-if="hasPreview" class="mt-4 preview">
                    <div class="preview-row preview-head">
                        <span></span>
                        <span>Current</span>
                        <span>Change</span>
                        <span>After</span>
                    </div>
                    <div v-for="line in previewLines" :key="line.label" class="preview-row">
                        <span class="preview-label">{{ line.label }}</span>
                        <span class="preview-now">{{ line.current }}</span>
                        <span :class="['preview-delta', line.deltaClass]">{{ line.delta }}</span>
                        <span class="preview-after">{{ line.after }}</span>
                    </div>
                    <p class="preview-note">
                        Totals for the period currently on screen, not just the selected day.
                    </p>
                </div>

                <!-- Optional volume nudges: clicks / FTDs / regs. Collapsed by
                     default — the three extra boxes read as required next to CPA
                     and made the common case confusing. Entering a CPA count
                     opens this and mirrors the count into FTDs, since one CPA is
                     one FTD; the other two stay blank unless you fill them. -->
                <div class="mt-4">
                    <button
                        type="button"
                        class="flex items-center gap-2 text-sm font-medium volume-toggle"
                        @click="volumeOpen = !volumeOpen"
                    >
                        <i :class="['pi', volumeOpen ? 'pi-chevron-down' : 'pi-chevron-right', 'text-xs']" />
                        <span>Volume (optional)</span>
                        <span v-if="!volumeOpen && volumeSummary" class="text-xs text-gray-400">
                            {{ volumeSummary }}
                        </span>
                    </button>

                    <div v-show="volumeOpen" class="mt-3">
                        <div class="grid grid-cols-3 gap-4">
                            <div class="flex flex-col">
                                <span class="text-xs text-gray-600 mb-1">Clicks</span>
                                <InputNumber
                                    v-model="clicksAdjustment"
                                    :minFractionDigits="0"
                                    :maxFractionDigits="0"
                                    class="w-full"
                                    placeholder="Clicks"
                                />
                            </div>
                            <div class="flex flex-col">
                                <span class="text-xs text-gray-600 mb-1">
                                    FTDs
                                    <span v-if="ftdsMirrored" class="text-gray-500">· mirroring CPA</span>
                                </span>
                                <InputNumber
                                    v-model="ftdsAdjustment"
                                    :minFractionDigits="0"
                                    :maxFractionDigits="0"
                                    class="w-full"
                                    placeholder="FTDs"
                                    @input="ftdsTouched = true"
                                />
                            </div>
                            <div class="flex flex-col">
                                <span class="text-xs text-gray-600 mb-1">Regs</span>
                                <InputNumber
                                    v-model="regsAdjustment"
                                    :minFractionDigits="0"
                                    :maxFractionDigits="0"
                                    class="w-full"
                                    placeholder="Regs"
                                />
                            </div>
                        </div>
                        <p class="text-xs text-gray-500 mt-1">
                            Negatives allowed. All go into one row for the selected date.
                        </p>
                    </div>
                </div>
            </div>
            <!-- Revenue Adjustment Inputs (only shown when Revenue adjustment is selected) -->
            <div v-if="selectedAdjustmentType === 'revenue'" class="mb-4">
                <div class="grid grid-cols-2 gap-4">
                    <div class="flex flex-col">
                        <label class="block text-sm font-medium mb-2">Publisher Revenue</label>
                        <InputNumber 
                            v-model="publisherRevenue" 
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                            class="w-full"
                            placeholder="Enter publisher revenue"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="block text-sm font-medium mb-2">Our Revenue</label>
                        <InputNumber 
                            v-model="ourRevenue" 
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                            class="w-full"
                            placeholder="Enter our revenue"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="p-4">
            <p>No data available</p>
        </div>
        
        <template #footer>
            <div class="flex justify-center items-center gap-2">
            <Button label="Save" severity="primary" icon="pi pi-check" :disabled="disabled" @click="saveAdjustment" />
            <Button label="Close" icon="pi pi-times" @click="close" text />
        </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';
import InputNumber from 'primevue/inputnumber';
import EarningsService from '@/services/EarningsService';
import { formatCurrency } from '@/utils/currencies';
import { getMetricsCustomerTotal, getMetricsOurTotal } from '@/utils/earnings';

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    rowData: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update:visible', 'savedAdjustment']);

const visible = ref(props.visible);
const selectedAdjustmentType = ref('cpa');
const selectedDate = ref(null);
const siteLinkData = ref(null);
const cpaAdjustment = ref(null);
const publisherRevenue = ref(null);
const ourRevenue = ref(null);
const clicksAdjustment = ref(null);
const ftdsAdjustment = ref(null);
const regsAdjustment = ref(null);
const adjustmentTypes = ref([
    { label: 'CPA Adjustment', value: 'cpa' },
    { label: 'Revenue Adjustment', value: 'revenue' }
]);

// Revenue the adjustment adds. Our cut is the SPREAD between the two deals, not
// our gross - the same arithmetic the server does in saveAdjustment, mirrored
// here so the preview cannot disagree with what gets saved.
/**
 * The calendar date the operator actually picked, as 'YYYY-MM-DD'.
 *
 * Sending the Date itself is what caused every Malta-made adjustment to be
 * stored a day early: axios serialises a Date with toISOString(), so picking
 * 18 Aug left the browser as 2026-08-17T22:00:00.000Z and the server read the
 * UTC day off it. Formatting from LOCAL getFullYear/getMonth/getDate keeps the
 * day the user saw in the picker, and the server parses a bare YYYY-MM-DD with
 * no timezone conversion.
 */
const toLocalYMD = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// The deal version in force on the selected day, when the server resolved one.
// Null for a link whose deal never changed — there the snapshot is still the truth.
const effectiveDeal = computed(() => siteLinkData.value?.effectiveDeal || null);

// The two rates the adjustment will actually be priced at. The server resolves
// them for the selected date and sends them back; reading them from anywhere
// else is what let the preview show v1's publisher rate beside v2's our rate.
const publisherDealCPA = computed(() =>
    effectiveDeal.value?.customerDeal?.cpa ?? siteLinkData.value?.siteBrandLink?.deal?.cpa ?? 0);

const ourDealCPA = computed(() => siteLinkData.value?.ourDeal?.ourDeal?.cpa ?? 0);

const dayLabel = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : toLocalYMD(d);
};
const selectedDateLabel = computed(() => dayLabel(selectedDate.value) || 'the selected day');
const effectiveFromLabel = computed(() => dayLabel(effectiveDeal.value?.effectiveFrom));

const customerCPA = computed(() => {
    if (!cpaAdjustment.value || !siteLinkData.value) return 0;
    return (cpaAdjustment.value * publisherDealCPA.value);
});

const ourCPA = computed(() => {
    if (!cpaAdjustment.value || !siteLinkData.value) return 0;
    return (cpaAdjustment.value * ourDealCPA.value) - customerCPA.value;
});

// --- Current vs after preview -------------------------------------------
// "Current" is the row the operator right-clicked, so it reflects the date
// range on screen behind the dialog. Labelled as such in the UI, because a
// per-day adjustment previewed against a period total would otherwise read as
// though the whole period were being replaced.
const currentMetrics = computed(() => props.rowData?.metrics || null);

const num = v => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
const fmtNum = v => num(v).toLocaleString('en-GB');
const fmtDelta = (v, money = false) => {
    if (!isSet(v)) return '—';
    const sign = v > 0 ? '+' : '−';
    return `${sign}${money ? formatCurrency(Math.abs(v)) : Math.abs(v).toLocaleString('en-GB')}`;
};

const hasPreview = computed(() =>
    !!currentMetrics.value && (isSet(cpaAdjustment.value) || isSet(clicksAdjustment.value)
        || isSet(ftdsAdjustment.value) || isSet(regsAdjustment.value)));

const previewLines = computed(() => {
    const m = currentMetrics.value;
    if (!m) return [];

    const lines = [];
    const push = (label, current, delta, money = false) => {
        // Only show rows this adjustment actually touches.
        if (!isSet(delta)) return;
        lines.push({
            label,
            current: money ? formatCurrency(num(current)) : fmtNum(current),
            delta: fmtDelta(delta, money),
            after: money ? formatCurrency(num(current) + delta) : fmtNum(num(current) + delta),
            deltaClass: delta > 0 ? 'is-up' : 'is-down',
        });
    };

    push('CPAs', m.CPA, cpaAdjustment.value);
    push('Clicks', m.clicks, clicksAdjustment.value);
    push('FTDs', m.FTD, ftdsAdjustment.value);
    push('Regs', m.signups, regsAdjustment.value);

    if (isSet(cpaAdjustment.value)) {
        push('Publisher rev', getMetricsCustomerTotal(m), customerCPA.value, true);
        push('Our rev', getMetricsOurTotal(m), ourCPA.value, true);
    }

    return lines;
});

// A field counts as "filled in" only when it's a non-zero number.
// 0 has no effect on totals, so we treat it as empty for enablement purposes.
const isSet = v => typeof v === 'number' && !Number.isNaN(v) && v !== 0;

// --- Volume section: collapsed until there's a reason to show it ---
const volumeOpen = ref(false);
// Set once the operator edits FTDs by hand. From then on we stop mirroring, so
// a deliberate value is never silently overwritten by a later CPA edit.
const ftdsTouched = ref(false);

const ftdsMirrored = computed(() =>
    !ftdsTouched.value && isSet(cpaAdjustment.value) && ftdsAdjustment.value === cpaAdjustment.value);

// Shown next to the collapsed header so hidden values are never a surprise.
const volumeSummary = computed(() => {
    const bits = [];
    if (isSet(clicksAdjustment.value)) bits.push(`${clicksAdjustment.value} clicks`);
    if (isSet(ftdsAdjustment.value)) bits.push(`${ftdsAdjustment.value} FTDs`);
    if (isSet(regsAdjustment.value)) bits.push(`${regsAdjustment.value} regs`);
    return bits.join(' · ');
});

/**
 * Mirror the CPA count into FTDs and reveal the section.
 *
 * One CPA is one FTD, so typing a count without the matching FTD was the usual
 * mistake. Mirroring is a default, not a rule: once ftdsTouched is set the
 * operator owns the field. Clearing CPA clears the mirrored value too — but
 * only while it is still a mirror, never a hand-entered one.
 */
watch(cpaAdjustment, (val) => {
    if (isSet(val)) {
        volumeOpen.value = true;
        if (!ftdsTouched.value) ftdsAdjustment.value = val;
        return;
    }
    // CPA cleared or zeroed.
    if (!ftdsTouched.value) {
        ftdsAdjustment.value = null;
        // Leave the section open if the operator put something in the other two.
        if (!isSet(clicksAdjustment.value) && !isSet(regsAdjustment.value)) {
            volumeOpen.value = false;
        }
    }
});

const disabled = computed(() => {
    if (!selectedAdjustmentType.value || !selectedDate.value) {
        return true;
    }

    if (selectedAdjustmentType.value === 'cpa') {
        // At least ONE of the four must be a real non-zero number.
        return !(isSet(cpaAdjustment.value)
            || isSet(clicksAdjustment.value)
            || isSet(ftdsAdjustment.value)
            || isSet(regsAdjustment.value));
    }

    if (selectedAdjustmentType.value === 'revenue') {
        return !(isSet(publisherRevenue.value) || isSet(ourRevenue.value));
    }

    return true;
});

const saveAdjustment = async () => {
    try {
        const response = await EarningsService.saveAdjustment(
            siteLinkData.value.siteBrandLink._id,
            selectedAdjustmentType.value,
            toLocalYMD(selectedDate.value),
            cpaAdjustment.value,
            publisherRevenue.value,
            ourRevenue.value,
            clicksAdjustment.value,
            ftdsAdjustment.value,
            regsAdjustment.value,
        );
        console.log('adjustment saved', response?.data?._id || response);
        emitSavedAdjustment();
    } catch (error) {
        console.error('Error saving adjustment:', error);
    }
};
/**
 * Clear every input back to its default.
 *
 * The dialog is reused across rows, and nothing previously cleared it — reopen
 * it for a different publisher and the last CPA count was still sitting there.
 * That was survivable while every field was visible; it is not once the volume
 * section can be collapsed, since a stale mirrored FTD count could be saved
 * against the wrong publisher without ever being on screen.
 */
const resetForm = () => {
    selectedDate.value = null;
    cpaAdjustment.value = null;
    publisherRevenue.value = null;
    ourRevenue.value = null;
    clicksAdjustment.value = null;
    ftdsAdjustment.value = null;
    regsAdjustment.value = null;
    volumeOpen.value = false;
    ftdsTouched.value = false;
};

watch(() => props.visible, (val) => {
    visible.value = val;

    if (val && props.rowData) {
        resetForm();
        fetchAdjustmentData();
    }
});

watch(selectedAdjustmentType, (newType) => {
    if (newType && props.rowData) {
        fetchAdjustmentData();
    }
});

// The rates depend on the day, so a date change has to re-ask the server.
watch(selectedDate, () => {
    if (props.rowData) fetchAdjustmentData();
});

const fetchAdjustmentData = async () => {
    try{
        const response = await EarningsService.getBySiteBrandLink(
            props.rowData.id,
            selectedDate.value ? toLocalYMD(selectedDate.value) : undefined
        );
        siteLinkData.value = response.data;
    } catch (error) {
        console.error('Error fetching adjustment data:', error);
    }
};

const emitSavedAdjustment = () => {
    emit('savedAdjustment');
};
const close = () => {
    visible.value = false;
    emit('update:visible', false);
};
</script>
<style scoped>
/* Plain button reset — this is a disclosure control, not an action button. */
.volume-toggle {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
}
.volume-toggle:hover {
    opacity: 0.8;
}

/* Current / Change / After. Four columns so the numbers line up as columns
   rather than reading as a sentence.
   Capped rather than fluid: the dialog runs to 60vw, and fr units stretched
   the three number columns so far apart that comparing current to after meant
   crossing half the screen. Fixed widths keep the comparison in one glance. */
.preview {
    border: 1px solid var(--p-content-border-color, #3f3f46);
    border-radius: 6px;
    overflow: hidden;
    max-width: 34rem;
}

.preview-row {
    display: grid;
    /* Label flexes; the three numeric columns are fixed so digits align down
       the column. 'After' gets a little extra for five-figure currency. */
    grid-template-columns: minmax(6rem, 1fr) 6.5rem 6.5rem 7rem;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem;
    align-items: baseline;
    font-size: 0.875rem;
}

.preview-row + .preview-row {
    border-top: 1px solid var(--p-content-border-color, #3f3f46);
}

.preview-head {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.6;
    background: rgba(255, 255, 255, 0.03);
}

.preview-label { opacity: 0.8; }

/* Digits in these three columns are compared down the column, so they need to
   line up regardless of glyph widths. */
.preview-now,
.preview-delta,
.preview-after {
    font-variant-numeric: tabular-nums;
    text-align: right;
}

.preview-now { opacity: 0.7; }
.preview-after { font-weight: 600; }
.preview-delta.is-up { color: #86efac; }
.preview-delta.is-down { color: #fca5a5; }

.preview-note {
    margin: 0;
    padding: 0.4rem 0.75rem 0.5rem;
    font-size: 0.7rem;
    opacity: 0.55;
}
</style>
